import type { CheckoutSession, Transaction } from '../api/types'

/**
 * Stripe Checkout redirect plumbing (legacy `PaymentService.setSessionId` /
 * `setLastPaymentPageUrl` + `location.href = result.url`). The card form
 * lives entirely on Stripe's hosted page — this app never sees card data
 * (audit §6: SAQ-A pattern, kept).
 *
 * Hardening over legacy (documented, no behaviour change for a real
 * session): the redirect target must be an absolute `https:` URL that the
 * BACKEND returned for the session just created — never a URL assembled
 * here — and the post-payment return URL is kept only if it is a same-origin
 * path (no open redirect through sessionStorage).
 */

export const sessionKey = (projectId: string) => `checkoutSessionId_${projectId}`
export const returnKey = (projectId: string) => `lastPaymentPageUrl_${projectId}`

function storage(): Storage | null {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

export function storedSessionId(projectId: string): string | null {
  return storage()?.getItem(sessionKey(projectId)) ?? null
}

export function clearStoredSession(projectId: string): void {
  storage()?.removeItem(sessionKey(projectId))
}

/** Reads AND clears the stored return path; only a same-origin path
 * (`/…`, not `//…`) is honoured, anything else falls back to `fallback`. */
export function takeReturnPath(projectId: string, fallback: string): string {
  const s = storage()
  const raw = s?.getItem(returnKey(projectId)) ?? ''
  s?.removeItem(returnKey(projectId))
  if (raw.startsWith('/') && !raw.startsWith('//')) return raw
  try {
    const u = new URL(raw)
    if (u.origin === window.location.origin) return `${u.pathname}${u.search}${u.hash}`
  } catch {
    // not a URL — fall back
  }
  return fallback
}

/** Indirection so tests can observe a navigation without jsdom performing
 * one. */
export const nav = {
  assign(url: string): void {
    window.location.assign(url)
  },
}

export class CheckoutRedirectError extends Error {}

/** Stores the session id + where to come back to, then leaves for Stripe. */
export function redirectToCheckout(projectId: string, session: CheckoutSession | null | undefined): void {
  const url = session?.url
  if (!session?.id || !url) throw new CheckoutRedirectError('The payment page could not be opened (no checkout URL).')
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    throw new CheckoutRedirectError('The payment page could not be opened (invalid checkout URL).')
  }
  if (parsed.protocol !== 'https:') {
    throw new CheckoutRedirectError('The payment page could not be opened (insecure checkout URL).')
  }
  const s = storage()
  s?.setItem(sessionKey(projectId), session.id)
  s?.setItem(returnKey(projectId), `${window.location.pathname}${window.location.search}`)
  nav.assign(parsed.toString())
}

/** Legacy `processBackgroundTransaction`: only a FAILED transaction
 * (status 9) produces a message. The legacy lookup of decline-code texts was
 * broken (it indexed by `stripeCode`, so it never matched); here the
 * decline code is shown as-is next to the Stripe code. */
export function failedTransactionMessage(tx: Transaction | null | undefined): string | null {
  if (!tx || Number(tx.status) !== 9) return null
  const details = (tx.details && !Array.isArray(tx.details) ? tx.details : {}) as Record<string, unknown>
  const stripeCode = typeof details.stripeCode === 'string' ? details.stripeCode : ''
  const declineCode = typeof details.declineCode === 'string' ? details.declineCode : ''
  const reason =
    Number(tx.result) === 2
      ? 'The card has expired.'
      : Number(tx.result) === 3
        ? 'The bank requires authentication — please pay with a new payment method.'
        : stripeCode === 'card_declined'
          ? `The card was declined${declineCode ? ` (${declineCode.replace(/_/g, ' ')})` : ''}.`
          : stripeCode
            ? `Stripe error: ${stripeCode.replace(/_/g, ' ')}.`
            : 'The payment could not be completed.'
  return `Transaction failed! ${reason}`
}
